// MorrowOS Knowledge base client helper.
//
// Reads the server proxy at:
//   GET /api/morrow/profile?userId=<id>            → the PERSONAL knowledge doc
//   GET /api/morrow/profile?orgId=<org_…>          → the ORGANIZATIONAL knowledge doc
//
// This returns the ever-growing profile/knowledge document MorrowOS builds
// about the user (stored server-side in R2). The call never throws — a failed
// fetch resolves to a structured payload so the knowledge UI keeps rendering.

import { apiUrl } from "./apiBase";
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";

/**
 * Auth headers for a profile/knowledge request. The global fetch-auth layer bails
 * in single-origin dev (paired browser: not cross-origin, not desktop), so the
 * Clerk token — which carries the active org and is required for org-gated reads —
 * wouldn't ride along, and the org doc 401s. Attach it explicitly here.
 */
async function knowledgeAuthHeaders(
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = await getClerkToken().catch(() => null);
  if (token) headers["x-clerk-token"] = token;
  const ownerId = getOwnerId();
  if (ownerId) headers["x-morrow-owner-id"] = ownerId;
  return headers;
}

export type KnowledgeErrorMessageKey =
  | "knowledge.error.forbidden"
  | "knowledge.error.notFound"
  | "knowledge.error.unavailable";

/** Map a knowledge error code to an i18n message key. */
export function knowledgeErrorMessageKey(errorCode: KnowledgeErrorCode): KnowledgeErrorMessageKey {
  switch (errorCode) {
    case KNOWLEDGE_ERROR_CODES.forbidden:
      return "knowledge.error.forbidden";
    case KNOWLEDGE_ERROR_CODES.notFound:
      return "knowledge.error.notFound";
    case KNOWLEDGE_ERROR_CODES.unavailable:
      return "knowledge.error.unavailable";
  }
}

/** Knowledge-probe state, surfaced so the page can say whether it's learning. */
export type KnowledgeProbeState = "idle" | "running" | "ready" | "failed";

export type KnowledgeErrorCode = "forbidden" | "not_found" | "unavailable";

export const KNOWLEDGE_ERROR_CODES = {
  forbidden: "forbidden",
  notFound: "not_found",
  unavailable: "unavailable",
} as const satisfies Record<string, KnowledgeErrorCode>;

export interface KnowledgeDoc {
  readonly exists: boolean;
  readonly markdown: string | null;
  readonly error: string | null;
  readonly errorCode: KnowledgeErrorCode | null;
  /** Whether the deep knowledge probe is running / done / couldn't gather. */
  readonly probeState: KnowledgeProbeState;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function asProbeState(value: unknown): KnowledgeProbeState {
  return value === "running" || value === "ready" || value === "failed" ? value : "idle";
}

/**
 * Which personal knowledge doc to read. The account keeps three companion docs:
 *   - "profile"     → durable FACTS ("About you").
 *   - "behavior"    → communication / working STYLE ("Your style").
 *   - "preferences" → accumulated likes / dislikes ("Preferences").
 * Mirrors the server's `?kind=` param on `GET /api/morrow/profile`.
 */
export type PersonalKnowledgeKind = "profile" | "behavior" | "preferences";

export async function fetchKnowledgeDoc(
  userId: string,
  kind: PersonalKnowledgeKind = "profile",
): Promise<KnowledgeDoc> {
  const trimmed = userId.trim();
  if (!trimmed) {
    return { exists: false, markdown: null, error: null, errorCode: null, probeState: "idle" };
  }
  // Omit `kind` for the default facts doc so the URL is byte-identical to the
  // pre-split call (and the server treats absent kind as "profile").
  const kindQuery = kind === "profile" ? "" : `&kind=${encodeURIComponent(kind)}`;
  return fetchProfile(`userId=${encodeURIComponent(trimmed)}${kindQuery}`);
}

/**
 * Read the ORGANIZATIONAL knowledge doc for an active Clerk org (`org_…`). Same
 * shape/behavior as {@link fetchKnowledgeDoc}, but sourced from the separate org
 * blob via the `orgId` query param.
 */
export async function fetchOrgKnowledgeDoc(orgId: string): Promise<KnowledgeDoc> {
  const trimmed = orgId.trim();
  if (!trimmed.startsWith("org_")) {
    return { exists: false, markdown: null, error: null, errorCode: null, probeState: "idle" };
  }
  return fetchProfile(`orgId=${encodeURIComponent(trimmed)}`);
}

/**
 * Re-run the profiling probe for an active org (admin action). Because the probe
 * now synthesizes BOTH lenses from one gather pass, this rebuilds the personal
 * doc (person lens — drops stale company content) AND the org doc (organization
 * lens). Fire-and-forget: the server returns 202 and writes to R2 in the
 * background; the caller flips to `probeState: "running"` and polls for the doc.
 * Requires `org:knowledge_base:manage` server-side (admins hold it). Returns
 * whether the probe was accepted (started), never throws.
 */
export async function rebuildOrgKnowledge(userId: string, orgId: string): Promise<boolean> {
  const uid = userId.trim();
  const oid = orgId.trim();
  if (!uid || !oid.startsWith("org_")) return false;
  try {
    const res = await fetch(apiUrl("/api/morrow/profile/probe"), {
      method: "POST",
      headers: await knowledgeAuthHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ userId: uid, orgId: oid, accountType: "company" }),
    });
    return res.ok;
  } catch (err) {
    console.warn("[knowledge] org rebuild error", err);
    return false;
  }
}

function knowledgeErrorForStatus(status: number): {
  error: string;
  errorCode: KnowledgeErrorCode;
} {
  if (status === 403) {
    return {
      error: KNOWLEDGE_ERROR_CODES.forbidden,
      errorCode: KNOWLEDGE_ERROR_CODES.forbidden,
    };
  }
  if (status === 404) {
    return {
      error: KNOWLEDGE_ERROR_CODES.notFound,
      errorCode: KNOWLEDGE_ERROR_CODES.notFound,
    };
  }
  return {
    error: KNOWLEDGE_ERROR_CODES.unavailable,
    errorCode: KNOWLEDGE_ERROR_CODES.unavailable,
  };
}

async function fetchProfile(queryString: string): Promise<KnowledgeDoc> {
  try {
    const res = await fetch(apiUrl(`/api/morrow/profile?${queryString}`), {
      headers: await knowledgeAuthHeaders(),
    });
    if (!res.ok) {
      const { error, errorCode } = knowledgeErrorForStatus(res.status);
      return {
        exists: false,
        markdown: null,
        error,
        errorCode,
        probeState: "idle",
      };
    }
    const body = (await res.json()) as Record<string, unknown> | null;
    const rec = body && typeof body === "object" ? body : {};
    const markdown = asString(rec.markdown);
    return {
      exists: rec.exists === true && markdown != null,
      markdown,
      error: null,
      errorCode: null,
      probeState: asProbeState(rec.probeState),
    };
  } catch (err) {
    console.warn("[knowledge] fetch error", err);
    return {
      exists: false,
      markdown: null,
      error: KNOWLEDGE_ERROR_CODES.unavailable,
      errorCode: KNOWLEDGE_ERROR_CODES.unavailable,
      probeState: "idle",
    };
  }
}
